import { setGlobalUser, resetGlobalUser, getUserState } from 'stores';
import { fetchWrapperPortal } from './fetch';
import { userAPI } from 'utils/portal-apis';
import { clearAuthStorage, getStoredUser } from './sessionStorage';
import type { User, LoginInputs } from '../../types';

declare global {
  interface Window {
    _hsq?: any[];
    HubSpotConversations?: {
      widget?: {
        refresh?: () => void;
      };
    };
  }
}

function isGuestUser(): boolean {
  return localStorage.getItem('is_guest_user') === 'true' || localStorage.getItem('is_guest_user') === 'true';
}

function onLoginResult(user: User | null, isGuestUserFlag = false): User | false {
  if (user && user._id) {
    localStorage.setItem('access_token', user.tokens!.access_token);
    localStorage.setItem('access_token_expires_at', String(user.tokens!.access_token_expires_at));

    // Handle both possible refresh token locations
    const refreshToken = user.refresh_token || user.tokens?.refresh_token;
    const refreshTokenExpiresAt = user.tokens!.refresh_token_expires_at;

    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    } else {
      console.warn('No refresh token found in response');
    }

    if (refreshTokenExpiresAt) {
      localStorage.setItem('refresh_token_expires_at', String(refreshTokenExpiresAt));
    }

    localStorage.setItem('is_guest_user', String(isGuestUserFlag));
  }
  userAPI.persistUser(user);
  return user ? user : false;
}

export const isLoggedIn = (): boolean => {
  if (isGuestUser()) {
    return false;
  }
  const exp = localStorage.getItem('access_token_expires_at');
  const currentTimeInSeconds = Math.floor(Date.now() / 1000);

  // Token expires_at is in seconds, compare it properly
  if (exp && parseInt(exp) > currentTimeInSeconds) {
    const userObj = getStoredUser<User>();

    // If there's no user object, return false
    if (!userObj) {
      return false;
    }

    // Only set user if not already set (prevent infinite re-renders)
    const currentUser = getUserState().user;
    if (!currentUser || !currentUser._id || currentUser._id !== userObj?._id) {
      setGlobalUser(userObj);
      const _hsq = (window._hsq = window._hsq || []);
      if (userObj?.email) {
        _hsq.push(['identify', { email: userObj.email }]);
      }

      window.HubSpotConversations?.widget?.refresh();
    }

    return true;
  }
  return false;
};

export const isRefreshable = (): boolean => {
  if (isGuestUser()) {
    return false;
  }
  const exp = localStorage.getItem('refresh_token_expires_at');
  const currentTimeInSeconds = Math.floor(Date.now() / 1000);

  // Refresh token expires_at is in seconds, compare it properly
  return exp ? parseInt(exp) > currentTimeInSeconds : false;
};

export const signup = async (inputs: LoginInputs): Promise<User | false> => {
  const user = await userAPI.signup(inputs);
  return onLoginResult(user);
};

export const login = async (inputs: LoginInputs): Promise<User | false> => {
  const result = await fetchWrapperPortal<User>({
    url: `auth/login/`,
    method: `POST`,
    body: JSON.stringify(inputs),
  });
  return onLoginResult(result);
};

export const oauthLogin = async (code: string): Promise<User | false> => {
  const result = await fetchWrapperPortal<User>({
    url: `auth/oauth/login`,
    method: `POST`,
    body: JSON.stringify({ code }),
  });

  return onLoginResult(result);
};

export const logout = async (): Promise<boolean> => {
  try {
    clearAuthStorage();

    // Reset global state
    resetGlobalUser();

    // Clear any existing HubSpot chat state
    const _hsq = (window._hsq = window._hsq || []);
    _hsq.push(['identify', null]);
    window.HubSpotConversations?.widget?.refresh();

    return true;
  } catch (error) {
    console.error('Error during logout:', error);
    throw error;
  }
};

export const getExternalToken = async (token: string): Promise<User | false> => {
  const result = await fetchWrapperPortal<User>({
    url: `users/me`,
    method: `GET`,
    addBearer: true,
    token: `Bearer ${token}`,
  });
  const now = new Date();
  const exp = Math.round(now.setDate(now.getDate() + 1) / 1000);
  return onLoginResult({ ...result, tokens: { access_token: token, access_token_expires_at: exp } });
};

export const needsRefresh = (): boolean => {
  if (isGuestUser()) return false;

  const expiresAt = localStorage.getItem('access_token_expires_at');
  if (!expiresAt) return false;

  const expiresAtTimestamp = parseInt(expiresAt, 10);
  const currentTime = Math.floor(Date.now() / 1000);
  const twoHours = 2 * 60 * 60; // 2 hours in seconds

  return expiresAtTimestamp - currentTime < twoHours;
};

export const refreshAuth = async (): Promise<User | false> => {
  try {
    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      console.warn('No refresh token available - keeping existing session');
      // Don't fail, just keep using existing token
      return false;
    }
    const params = await fetchWrapperPortal<User>({
      url: `auth/refresh/`,
      method: `POST`,
      headers: {
        Authorization: `Bearer ${refresh_token}`,
      },
    });

    if (!params || !params.tokens || !params.tokens.access_token) {
      console.warn('Invalid refresh response, keeping existing session:', params);
      // Don't clear tokens - keep existing session alive
      return false;
    }

    return onLoginResult(params);
  } catch (error) {
    const isNetworkError =
      (error as Error).name === 'TypeError' ||
      (error as Error).name === 'NetworkError' ||
      (error as Error).message?.includes('fetch');

    if (isNetworkError) {
      // Network issue — keep existing session, don't clear anything
      console.warn('Token refresh failed due to network issue - keeping existing session');
      return false;
    }

    // Server actively rejected the refresh token (401, 403, etc.)
    // Clear auth state so the user isn't stuck in a loop
    console.warn('Token refresh rejected by server - clearing session:', error);
    clearAuthStorage();
    resetGlobalUser();
    return false;
  }
};

export const getResetToken = async (token: string): Promise<any> =>
  await fetchWrapperPortal({
    url: `auth/password/reset/${token}`,
    method: `GET`,
    addBearer: false,
  });

export const adminResetPassword = async ({
  adminId,
  userId,
  newPassword,
}: {
  adminId: string;
  userId: string;
  newPassword: string;
}): Promise<any> => {
  return await fetchWrapperPortal({
    url: `auth/admin/password/reset`,
    method: `PUT`,
    body: JSON.stringify({ adminId, userId, newPassword }),
    addBearer: true,
  });
};
